import { Tooltip } from "antd/es";
import React from "react";
import { useMeasure } from "react-use";

import { IconEnlarge } from "~/components/icon";
import { Truncated } from "~/components/truncated";
import { ZIndexBoundary } from "~/components/z-index-boundary";
import type { BoundMetricChartType } from "~/tools/bound-metric";
import { cx } from "~/tools/class-variance";
import type { Time } from "~/tools/date-time/time";
import { floor } from "~/tools/floor";
import { Impulse, isImpulse } from "~/tools/impulse";
import { prop } from "~/tools/prop";
import { Template } from "~/tools/template";

import { normalizePinnedMetricComponent } from "../../../bottom_content/view/panes/east/pinned-items/PinnedItem";
import { PinningButton } from "../../../bottom_content/view/panes/east/pinned-items/PinningButton";
import { generatePath } from "../../../generic/routing";
import { queryUIConfig } from "../../../queries";
import { useQuery } from "../../../queries/react-query";
import { COMPONENT_PATH } from "../../../routes/Routes";
import { useComponentBoundary } from "../../_component-boundary";
import { LockedMetric } from "../../_locked-metric";
import { GaugeChart } from "../../gauge-chart";
import { CtxMenuItem } from "../CtxMenu/CtxMenuItem";
import { CtxPopoverMenu } from "../CtxMenu/CtxPopoverMenu";
import { useExplorationDrawerState } from "../drawer/use-exploration-drawer-state";

import { ThumbnailChartContainer, type ThumbnailChartContainerProps } from "./_thumbnail-chart-container";

export interface ThumbnailTimeSeriesChartRef {
	width: number;
}

export interface ThumbnailTimeSeriesChartProps
	extends Omit<ThumbnailChartContainerProps, "onOpenMetricsDrawer" | "width" | "height"> {
	title?: string;
	description?: string;
	markerTimestamp?: Time;
	isLocked?: boolean;

	/**
	 * @default "Line"
	 */
	chartType?: BoundMetricChartType;
	className?: string;
}

export const ThumbnailTimeSeriesChart = React.forwardRef<ThumbnailTimeSeriesChartRef, ThumbnailTimeSeriesChartProps>(
	(
		{
			className,
			title,
			description,
			unit,
			metricQueries,
			timeRange,
			yAxis,
			legend,
			retry,
			batch,
			namespace,
			markerTimestamp,
			isLocked = false,
			chartType = "Line",
			type,
			connectNulls,
			thresholdLines,
			...props
		},
		ref,
	) => {
		const explorationDrawer = useExplorationDrawerState();
		const component = normalizePinnedMetricComponent(useComponentBoundary());

		const onOpenMetricsDrawer = () => {
			explorationDrawer.openMetrics({
				metricQueries,
				timeRange: isImpulse(timeRange) ? timeRange.clone() : Impulse(timeRange),
				title,
				unit,
				componentIdOrIdentifier: component?.identifier,
				timestamp: markerTimestamp,
			});
		};

		const { data: webUIConfig } = useQuery({
			...queryUIConfig,
			select: prop("webUIConfig"),
		});
		const [containerRef, { width: containerWidth }] = useMeasure<HTMLDivElement>();
		const roundedWidth = floor(containerWidth, 0);

		const isGaugeChart = chartType === "Gauge" && metricQueries.length > 0;

		React.useImperativeHandle(ref, () => ({ width: roundedWidth }), [roundedWidth]);

		return (
			<div
				data-testid="thumbnail-chart"
				ref={containerRef}
				{...props}
				className={cx("flex flex-col space-y-3", className)}
			>
				{title && (
					<ThumbnailChartTitle description={description} title={title}>
						{!isLocked && component && (
							<PinningButton item={{ name: title, description, unit, metricQueries, component, chartType }} onlyIcon />
						)}

						{!(isLocked || isGaugeChart) && (
							<Tooltip title="Open in metric inspector">
								<div className="flex items-center" onClick={onOpenMetricsDrawer}>
									<IconEnlarge className="cursor-pointer text-gray-800 hover:text-primary-500" />
								</div>
							</Tooltip>
						)}

						{webUIConfig?.featureFlags.dashboards && !isLocked && (
							<ZIndexBoundary zIndex={(closestZIndex = 0) => closestZIndex + 50}>
								{(zIndex) => (
									<CtxPopoverMenu ellipsisClassName="m-0" zIndex={zIndex}>
										<CtxMenuItem
											label="Add to dashboard"
											onClick={() => {
												explorationDrawer.openAddToDashboard([
													{
														name: Template(title),
														plugin: {
															kind: "TimeSeriesChart",
															spec: {
																yAxis: {
																	format: unit == null ? undefined : { unit },
																},
															},
															queries: metricQueries.map(({ promql, label, step }) => ({
																query: Template(promql),
																alias: label,
																minStep: step == null ? undefined : Template(step.toString()),
															})),
															links: component
																? [
																		{
																			url: Template(
																				`/#${generatePath(COMPONENT_PATH, { identifier: component.identifier })}`,
																			),
																			name: Template(`${component.name} (${component.type})`),
																			targetBlank: false,
																		},
																	]
																: [],
														},
													},
												]);
											}}
										/>
									</CtxPopoverMenu>
								)}
							</ZIndexBoundary>
						)}
					</ThumbnailChartTitle>
				)}

				{isLocked ? (
					<LockedMetric size="lg" title={title ?? ""} />
				) : isGaugeChart ? (
					<GaugeChart
						className="flex-1"
						namespace={namespace}
						query={metricQueries[0]}
						retry={retry}
						timestamp={markerTimestamp}
						unit={unit}
					/>
				) : (
					<ThumbnailChartContainer
						batch={batch}
						connectNulls={connectNulls}
						legend={legend}
						markerTimestamp={markerTimestamp}
						metricQueries={metricQueries}
						namespace={namespace}
						onOpenMetricsDrawer={onOpenMetricsDrawer}
						retry={retry}
						thresholdLines={thresholdLines}
						timeRange={timeRange}
						type={type}
						unit={unit}
						width={roundedWidth}
						yAxis={yAxis}
					/>
				)}
			</div>
		);
	},
);

const ThumbnailChartTitle: React.FC<
	React.PropsWithChildren<{
		title: string;
		description: undefined | string;
	}>
> = ({ title, description, children }) => (
	<div
		className="flex flex-row justify-between gap-6 font-normal text-13/5 text-gray-800"
		data-testid="thumbnail-chart-header"
	>
		<Truncated tooltip={description ? `${title} - ${description}` : title}>{title}</Truncated>

		<div className="flex gap-4">{children}</div>
	</div>
);
